import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Search,
  Layers,
  ArrowUpRight,
  FileText,
  CheckCircle2,
  Target,
  Table,
  Compass,
  BookOpen,
  Award,
  Lightbulb
} from "lucide-react";

export default function ContentWritingAiVisibilityPostPage() {
  const [copied, setCopied] = useState(false);
  const [checkedQuestions, setCheckedQuestions] = useState({});

  const toggleQuestion = (id) => {
    setCheckedQuestions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const originalTitle = document.title;
    const pageTitle =
      "How to Write Content That Ranks on Google and Improves AI Visibility | Kanna Narendra Kumar";
    const pageDesc =
      "Good content should help your target audience make a decision, not just explain how great your product is. Learn the 10 principles to write content that ranks on Google and improves AI visibility across ChatGPT, Google AI Overviews, Gemini, and Perplexity.";
    const pageUrl =
      "https://narendradigital.in/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/";

    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + BreadcrumbList + FAQPage
    const scriptId = "content-writing-ai-visibility-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in/"
            },
            "headline": "How to Write Content That Ranks on Google and Improves AI Visibility",
            "description": pageDesc,
            "image": "https://narendradigital.in/og-image-v3.jpg",
            "datePublished": "2026-11-03T10:00:00+05:30",
            "dateModified": "2026-11-03T16:15:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "How to Write Content That Ranks on Google and Improves AI Visibility",
              "SEO Content Writing Strategy",
              "AEO Answer Engine Optimization",
              "GEO Generative Engine Optimization",
              "AI Visibility Content Strategy",
              "ChatGPT Citations Content",
              "Google AI Overviews Content Optimization",
              "Decision-Focused Content Writing",
              "Hybrid Engine Optimization HEO"
            ],
            "articleSection": "AEO/GEO , AI Visibility",
            "inLanguage": "en-US"
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "How to Write Content That Ranks on Google and Improves AI Visibility",
                "item": pageUrl
              }
            ]
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Do I need to write separate articles for SEO, AEO, and GEO?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. You do not need to write separate articles for SEO, AEO (Answer Engine Optimization), and GEO (Generative Engine Optimization). Your content should serve the same end user across traditional Google search, Google AI Overviews, ChatGPT, Gemini, and Perplexity by providing useful, accurate, and relevant decision-helping information."
                }
              },
              {
                "@type": "Question",
                "name": "How do you choose content topics that improve both Google rankings and AI visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Choose topics based on real user intent—the questions, challenges, and decisions your target audience faces—rather than product features or search volume alone. Research real problems across Google Search Console, Reddit, Quora, People Also Ask, customer reviews, sales calls, and competitor reviews."
                }
              },
              {
                "@type": "Question",
                "name": "Why is supporting content with evidence important for AI visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Evidence such as customer case studies, real reviews, original research, statistics, and product demonstrations shows readers why they should trust your claims and makes your content a high-credibility source for generative AI responses to reference and cite."
                }
              },
              {
                "@type": "Question",
                "name": "What is the best article structure for SEO and AI visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "While structure should flex to match user intent, a strong starting framework includes: A. Direct Introduction, B. Main problem or pain point, C. Possible solutions across scenarios, D. Comparisons and original insights supported by evidence, E. Natural product relevance, and F. Actionable conclusion."
                }
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selfCheckQuestions = [
    {
      id: "q1",
      question: "1. Is this a topic my target audience actually wants to know about?",
      hint: "Selected from real buyer challenges rather than internal product priorities."
    },
    {
      id: "q2",
      question: "2. What decision is the reader trying to make?",
      hint: "Helps the reader move forward and evaluate options clearly."
    },
    {
      id: "q3",
      question: "3. Am I answering the main question directly?",
      hint: "Gets straight to the point without fluff or unnecessary word-count padding."
    },
    {
      id: "q4",
      question: "4. Have I included useful insights that go beyond existing information?",
      hint: "Includes practical industry observations, common mistakes, and real experience."
    },
    {
      id: "q5",
      question: "5. Have I covered the possible scenarios and follow-up questions?",
      hint: "Accounts for different business sizes, use cases, and next-step questions."
    },
    {
      id: "q6",
      question: "6. Have I supported my statements with reliable evidence?",
      hint: "Backed by case studies, customer reviews, data, or product demonstrations."
    },
    {
      id: "q7",
      question: "7. Does my content help the reader evaluate possible solutions?",
      hint: "Uses structured comparisons and clear pros/cons to aid decision-making."
    },
    {
      id: "q8",
      question: "8. Is my product included naturally where it is relevant?",
      hint: "Introduced as a connected workflow solution rather than forced into every paragraph."
    }
  ];

  const completedCount = Object.values(checkedQuestions).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-indigo-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed top-96 right-10 w-[500px] h-[350px] bg-cyan-600/10 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Global Navigation Bar */}
      <Navbar />

      <main className="max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 relative z-10">
        {/* Article Breadcrumbs & Quick Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <nav className="flex items-center gap-2 text-xs font-mono text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <Link to="/blog/" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <span className="text-cyan-400 truncate">AEO / GEO &amp; AI Visibility</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-cyan-400 px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Blog</span>
            </Link>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-cyan-400 hover:text-white px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </div>

        {/* Article Header */}
        <header className="space-y-6 pb-8 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider">
              AEO/GEO , AI Visibility
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
              10 Content Strategy Principles
            </span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.15]">
            How to Write Content That Ranks on Google and Improves AI Visibility
          </h1>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 text-xs font-mono text-white/50">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/80 font-bold">Kanna Narendra Kumar</span>
              <span>&bull;</span>
              <span className="text-cyan-400">Senior SEO Specialist &amp; AI Search Strategist</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>Nov 03, 2026</span>
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span>11 min read</span>
              </span>
            </div>
          </div>
        </header>

        {/* Key Takeaway Box */}
        <section
          aria-label="Key Takeaway"
          className="my-7 p-5 sm:p-6 rounded-xl bg-[#0c1626] border-2 border-cyan-400 shadow-lg shadow-cyan-950/20"
        >
          <div className="flex items-center gap-2 mb-3 text-cyan-400">
            <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
            <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-cyan-400 m-0">
              Key Takeaway
            </h2>
          </div>

          <p className="text-sm sm:text-base text-white leading-relaxed font-medium m-0">
            Good content should help your target audience make a decision, not just explain how great your product is. To improve your SEO rankings and AI visibility across platforms like ChatGPT, Google AI Overviews, Gemini and Perplexity, create content around real user problems, provide practical insights, explain possible solutions and support your information with evidence.
          </p>
        </section>

        {/* Visual Overview of the 10 Principles */}
        <div className="my-8 p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-[#0c1427] to-[#09101d] border border-cyan-500/20">
          <div className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Compass className="w-4 h-4" />
            <span>The 10 Principles for SEO &amp; AI Visibility Content</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5 text-xs">
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-cyan-400 font-bold block mb-1">01. User Intent</span>
              <p className="text-[11px] text-white/70">Pick topics around buyer decisions, not product features</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-indigo-400 font-bold block mb-1">02. Direct Answers</span>
              <p className="text-[11px] text-white/70">Get straight to the point without word-count fluff</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-purple-400 font-bold block mb-1">03. Own Insights</span>
              <p className="text-[11px] text-white/70">Add real industry observations &amp; lessons learned</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-amber-400 font-bold block mb-1">04. Problem-Solving</span>
              <p className="text-[11px] text-white/70">Address pain points &amp; anticipate follow-up questions</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-emerald-400 font-bold block mb-1">05. Scenarios</span>
              <p className="text-[11px] text-white/70">Map solutions to different business situations</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-cyan-400 font-bold block mb-1">06. Comparisons</span>
              <p className="text-[11px] text-white/70">Use structured comparison tables where helpful</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-indigo-400 font-bold block mb-1">07. Evidence</span>
              <p className="text-[11px] text-white/70">Back claims with case studies, reviews &amp; data</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-purple-400 font-bold block mb-1">08. Product Fit</span>
              <p className="text-[11px] text-white/70">Introduce your product naturally as a relevant solution</p>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10">
              <span className="font-mono text-amber-400 font-bold block mb-1">09. Clear Structure</span>
              <p className="text-[11px] text-white/70">Follow a flexible, intent-driven content flow</p>
            </div>
            <div className="p-3 rounded-xl bg-cyan-950/40 border border-cyan-500/40">
              <span className="font-mono text-emerald-300 font-bold block mb-1">10. Credibility</span>
              <p className="text-[11px] text-white/80">Include expert insights &amp; a real author profile</p>
            </div>
          </div>
        </div>

        {/* Article Body */}
        <article className="py-6 space-y-8 text-white/85 text-base sm:text-lg leading-relaxed font-sans">
          {/* Introduction */}
          <div className="space-y-4">
            <p>
              Content writing is no longer just about adding keywords, optimising headings and publishing articles to improve Google rankings.
            </p>
            <p>
              With the growing importance of AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization), businesses also need to think about how their content is understood, selected, mentioned and referenced by AI platforms.
            </p>
            <p>
              But this doesn&apos;t mean you need to write separate articles for SEO, AEO and GEO.
            </p>
            <p>
              Your content should serve the same end user across all these discovery experiences.
            </p>
            <p>
              Whether someone finds your article through a traditional Google search, gets an answer through Google AI Overviews or receives a recommendation from ChatGPT, the underlying requirement remains the same: your content should provide useful, accurate and relevant information.
            </p>
            <p className="font-semibold text-white">
              So, how do you create content that performs well in SEO while also improving your AI visibility?
            </p>
            <p>
              Here are the ten principles I follow when developing a content strategy.
            </p>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Principle 1 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              1. Choose the Right Topic Based on User Intent, Not Product Priorities
            </h2>
            <p>
              One of the biggest mistakes businesses make is choosing content topics based on what they already know about their products.
            </p>
            <p>
              Sometimes, the product team suggests topics based on product features. Sometimes, the marketing team looks at competitor blogs and decides to write about the same subjects.
            </p>
            <p>
              But is that what your target audience actually wants to know?
            </p>
            <p>Your content topic should not be selected simply because:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/80">
              <li>Your business knows a lot about that subject.</li>
              <li>Your product team wants to promote a particular feature.</li>
              <li>Your competitors have already published an article about it.</li>
              <li>A keyword research tool shows good search volume.</li>
            </ul>
            <p>
              Instead, choose topics based on the questions, challenges and decisions your target audience faces.
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-lg sm:text-xl shadow-xl my-6 not-italic">
              Your content should help users make a decision.
            </blockquote>
            <p>
              For example, imagine you are marketing an HRMS software product.
            </p>
            <p>
              Instead of writing another article explaining the features of your HRMS software, think about the problems an HR manager faces:
            </p>
            <div className="p-5 rounded-2xl bg-[#091122] border border-cyan-500/20 space-y-2">
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-200">
                <li>How do I manage employee attendance across multiple locations?</li>
                <li>What should I consider before choosing an HRMS?</li>
                <li>How can I reduce manual payroll errors?</li>
                <li>What are the challenges of managing employee records manually?</li>
                <li>When should a growing business move from spreadsheets to HRMS software?</li>
              </ul>
            </div>
            <p>
              These questions give you much stronger content opportunities because they are connected to actual user problems and decisions.
            </p>

            <h3 className="font-heading font-bold text-xl sm:text-2xl text-white pt-2">
              How do you find the right topics?
            </h3>
            <p>
              Don&apos;t depend entirely on keyword research tools such as Semrush and Ahrefs to discover what your audience wants to know.
            </p>
            <p>
              You can use them to understand keyword opportunities, competition and search demand. However, you also need to research the questions and problems your target audience is discussing.
            </p>
            <p>Here are some places to start:</p>
            <ul className="list-disc pl-5 space-y-2 text-white/85">
              <li>
                <strong className="text-white">Google Search Console:</strong> Identify queries that already bring relevant visitors to your website.
              </li>
              <li>
                <strong className="text-white">Reddit and Quora:</strong> Find real questions, discussions and problems from your target audience.
              </li>
              <li>
                <strong className="text-white">Google Search:</strong> Explore People Also Ask, autocomplete and related searches.
              </li>
              <li>
                <strong className="text-white">Customer reviews:</strong> Understand what customers appreciate, dislike and struggle with.
              </li>
              <li>
                <strong className="text-white">Sales and customer support teams:</strong> Identify frequently asked questions and objections.
              </li>
              <li>
                <strong className="text-white">Competitor reviews:</strong> Understand what customers are missing from existing solutions.
              </li>
            </ul>
            <p>
              I have explained this approach in more detail in my previous article:{" "}
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                How to Do Keyword Research for AI Search
              </Link>
            </p>
            <p>
              The idea is simple: identify the questions your audience is asking, understand the decisions behind those questions and create content that helps them move forward.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 2 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              2. Get Straight to the Point: Don&apos;t Make Readers Search for Answers
            </h2>
            <p>
              You don&apos;t need to build a story before answering a user&apos;s question.
            </p>
            <p>
              If someone searches for a solution to a problem, they expect useful information, not three paragraphs explaining why the topic is important.
            </p>
            <p>
              Start with the actual problem and address it directly.
            </p>
            <p>
              For example, if your article is about reducing payroll errors, don&apos;t start with a generic introduction about how payroll management is important for every organisation.
            </p>
            <p>
              Instead, explain why payroll errors happen, what problems they create and how businesses can prevent them.
            </p>
            <p>A good introduction should help readers understand:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>What problem does this article address?</li>
              <li>Why should the reader care?</li>
              <li>What will the reader learn or be able to decide after reading it?</li>
            </ul>
            <p>
              This approach is also useful for AEO and GEO. Clearly written answers and well-organised information make it easier for readers and AI systems to understand what your content is about.
            </p>
            <p>
              You also don&apos;t need to write 2,000 words just because someone says long-form content is good for SEO.
            </p>
            <p>
              If you can answer the user&apos;s question comprehensively in 800 words, there is no reason to stretch it to 2,000 words.
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-base sm:text-lg shadow-xl my-6 not-italic">
              Write as much as the topic requires, not as much as an SEO word-count recommendation suggests.
            </blockquote>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 3 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              3. Add Your Own Insights to the Content
            </h2>
            <p>
              Information alone is not always enough to make your article valuable.
            </p>
            <p>
              If someone can find exactly the same information in ten other articles, why should they choose yours?
            </p>
            <p>
              This is where your insights become important.
            </p>
            <p>
              An insight can come from your professional experience, customer interactions, research, industry observations or an understanding of how a particular problem works in real situations.
            </p>
            <p>
              For example, an article about choosing HRMS software could explain the common features available in the market.
            </p>
            <p>
              But you can make it more useful by adding an observation such as:
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-medium text-base sm:text-lg shadow-xl my-6">
              &ldquo;Many growing businesses compare HRMS platforms based on the number of features they offer. However, the more important consideration is whether the software can handle the organisation&apos;s actual approval workflows, employee structure and payroll requirements.&rdquo;
            </blockquote>
            <p>
              This additional perspective helps the reader evaluate the decision instead of simply consuming information.
            </p>
            <p>Try to include:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Practical observations from your industry.</li>
              <li>Common mistakes people make.</li>
              <li>Lessons from real customer experiences.</li>
              <li>Your own interpretation of industry trends.</li>
              <li>Examples that explain why a particular approach works or fails.</li>
            </ul>
            <p>
              Don&apos;t add opinions just to make the article look different. Your insights should add value to the topic.
            </p>
            <p>
              Original insights and first-hand experience also give your content something more valuable than generic information that can be reproduced across multiple websites. This can strengthen your GEO content strategy by giving readers and AI platforms useful information to reference.
            </p>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>AI Search Readiness Check</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Is Your Website Being Cited in ChatGPT, Google AI Overviews &amp; Claude?
              </h3>
              <p className="text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed">
                Uncover technical crawler blockers, JavaScript rendering gaps, and high-intent customer fan-out opportunities with a comprehensive AI Visibility Audit.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Principle 4 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              4. Make Your Content Problem-Solving, Not Product-Selling
            </h2>
            <p>
              Your article should not be written with the intention of selling your product in every paragraph.
            </p>
            <p>
              Its primary purpose should be to help users understand their problems and evaluate possible solutions.
            </p>
            <p>Let&apos;s take an example.</p>
            <p>
              Suppose you are writing about travel and expense management software.
            </p>
            <p>
              Instead of immediately introducing your product and explaining its features, first address the problems businesses face:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Employees submitting expenses without proper receipts.</li>
              <li>Delays in travel approvals.</li>
              <li>Difficulty tracking travel budgets.</li>
              <li>Manual reimbursement calculations.</li>
              <li>Lack of visibility into employee spending.</li>
            </ul>
            <p>Then explain how these problems can be solved.</p>
            <p>
              You can break the solution into different approaches, explain the advantages and limitations of each and help readers understand which approach might work for their business.
            </p>
            <p>Also, think beyond the first question.</p>
            <p>
              If someone asks, &ldquo;How can I automate employee expense management?&rdquo;, their next questions might be:
            </p>
            <div className="p-5 rounded-2xl bg-[#091122] border border-indigo-500/20 space-y-2">
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-200">
                <li>What processes can be automated?</li>
                <li>How much manual work can be reduced?</li>
                <li>What should I look for in expense management software?</li>
                <li>How do I choose the right solution?</li>
              </ul>
            </div>
            <p>
              Your article should anticipate these related questions wherever they are relevant.
            </p>
            <p>
              This makes the content more useful because you are helping the reader progress through the decision-making process rather than answering just one isolated question.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 5 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              5. Explain Different Scenarios and Possible Solutions
            </h2>
            <p>
              Not every user faces the same problem, even when they search for the same topic.
            </p>
            <p>
              Your content should account for the different situations in which the problem occurs.
            </p>
            <p>
              For example, consider an article about choosing payroll software.
            </p>
            <p>
              A startup with 15 employees may have completely different requirements from an organisation with 500 employees.
            </p>
            <p>You can break the topic into different scenarios:</p>

            {/* Scenario Table */}
            <div className="my-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Business situation</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">Possible challenge</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">What to consider</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Small business</td>
                    <td className="p-3.5">Managing payroll manually</td>
                    <td className="p-3.5 text-emerald-200">Basic payroll automation</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Growing business</td>
                    <td className="p-3.5">Increasing employee records and calculations</td>
                    <td className="p-3.5 text-emerald-200">Scalability and workflow automation</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Multi-location business</td>
                    <td className="p-3.5">Different attendance and payroll requirements</td>
                    <td className="p-3.5 text-emerald-200">Centralised employee management</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Large organisation</td>
                    <td className="p-3.5">Complex approval and compliance processes</td>
                    <td className="p-3.5 text-emerald-200">Integration, reporting and custom workflows</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              This approach helps readers understand which solution is relevant to their circumstances.
            </p>
            <p>
              It also makes your article more comprehensive without adding unnecessary information.
            </p>
            <p>Whenever you write about a topic, ask yourself:</p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-base sm:text-lg shadow-xl my-6 not-italic">
              What are the possible situations in which someone might face this problem, and how can I help them solve each one?
            </blockquote>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 6 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              6. Use Comparative Content Wherever It Helps the Reader
            </h2>
            <p>
              Comparison is an important part of decision-making.
            </p>
            <p>
              People often search for information because they are evaluating different approaches, products or solutions.
            </p>
            <p>You should make these comparisons easy to understand.</p>
            <p>
              For example, if you are writing about employee attendance management, you could compare manual attendance tracking, spreadsheets and automated attendance software.
            </p>

            {/* Comparison Table */}
            <div className="my-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#090f1e]">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.04]">
                    <th className="p-3.5 font-mono text-cyan-300 font-bold uppercase">Comparison factor</th>
                    <th className="p-3.5 font-mono text-white/80 font-bold uppercase">Manual tracking</th>
                    <th className="p-3.5 font-mono text-amber-300 font-bold uppercase">Spreadsheet</th>
                    <th className="p-3.5 font-mono text-emerald-300 font-bold uppercase">Attendance software</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] text-white/80">
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Data entry</td>
                    <td className="p-3.5">Manual</td>
                    <td className="p-3.5">Manual</td>
                    <td className="p-3.5 text-emerald-200">Automated options</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Error management</td>
                    <td className="p-3.5">Difficult</td>
                    <td className="p-3.5">Requires manual checks</td>
                    <td className="p-3.5 text-emerald-200">Validation and alerts</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Reporting</td>
                    <td className="p-3.5">Manual preparation</td>
                    <td className="p-3.5">Basic reporting</td>
                    <td className="p-3.5 text-emerald-200">Automated reporting</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Scalability</td>
                    <td className="p-3.5">Limited</td>
                    <td className="p-3.5">Becomes difficult</td>
                    <td className="p-3.5 text-emerald-200">Depends on the platform</td>
                  </tr>
                  <tr>
                    <td className="p-3.5 font-semibold text-white">Centralised access</td>
                    <td className="p-3.5">Limited</td>
                    <td className="p-3.5">Depends on setup</td>
                    <td className="p-3.5 text-emerald-200">Usually available</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              A well-structured comparison table helps readers understand the differences without reading multiple paragraphs.
            </p>
            <p>You can also use comparisons within your content to explain:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Different methods of solving a problem.</li>
              <li>The advantages and disadvantages of each approach.</li>
              <li>When one solution is more suitable than another.</li>
              <li>What users should consider before making a decision.</li>
            </ul>
            <p>
              Don&apos;t create comparison tables simply for the sake of adding tables. Use them when they make information easier to understand.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 7 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              7. Support Your Content With Evidence
            </h2>
            <p>
              This is one of the most important things I would recommend when creating content for both traditional SEO and AI visibility.
            </p>
            <p>
              Don&apos;t just make statements. Support them with evidence wherever possible.
            </p>
            <p>
              For example, if you claim that a particular process reduces manual work, explain how you arrived at that conclusion.
            </p>
            <p>You can support your content with:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Customer case studies.</li>
              <li>Real customer reviews and testimonials.</li>
              <li>Original research and survey findings.</li>
              <li>Industry reports and statistics.</li>
              <li>Product demonstrations and screenshots.</li>
              <li>Expert opinions and interviews.</li>
              <li>Relevant external research.</li>
            </ul>
            <p>
              Imagine you are writing about how businesses can reduce payroll processing time.
            </p>
            <p>
              Instead of simply saying that automation saves time, include a relevant customer example showing the previous process, the changes implemented and the results achieved.
            </p>
            <p>
              A case study or customer review can provide useful supporting evidence that a generic statement cannot.
            </p>
            <p>
              Make sure that statistics and performance claims are accurate, traceable and presented with the right context.
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-base sm:text-lg shadow-xl my-6 not-italic">
              Your content should not just tell readers what works. It should show them why they should trust that information.
            </blockquote>
            <p>
              Evidence, original data and real-world examples can also make your content more valuable as a potential source for generative AI responses.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 8 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              8. Explain Where Your Product Fits Into the Solution
            </h2>
            <p>
              There is nothing wrong with introducing your product in an informational article.
            </p>
            <p>
              However, the product should appear as a relevant solution to the problem being discussed, not as the reason the article exists.
            </p>
            <p>
              For example, after explaining the challenges of managing employee travel requests and expense reimbursements, you can introduce how your product addresses those workflows.
            </p>
            <p>You could explain:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Which specific problem your product solves.</li>
              <li>How the product handles that particular workflow.</li>
              <li>What makes its approach useful.</li>
              <li>Which type of business would benefit from it.</li>
            </ul>
            <p>
              You can also include a small comparison with other solutions available in the market.
            </p>
            <p>For example:</p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-medium text-base sm:text-lg shadow-xl my-6">
              &ldquo;Unlike basic expense tracking tools, EMgage brings travel requests, expense submissions, approvals and reimbursements into a connected workflow.&rdquo;
            </blockquote>
            <p>
              Only use comparisons that you can substantiate. If you mention competitors or alternative products, make sure the comparison is fair and accurate.
            </p>
            <p>
              You don&apos;t need to turn the entire article into a product promotion. A relevant product explanation, supported by a case study or customer review, is often more useful than repeatedly inserting product CTAs.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 9 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              9. Follow a Simple Content Structure
            </h2>
            <p>
              There is no single article structure that works for every topic.
            </p>
            <p>
              The structure should depend on what the reader wants to understand and the decision they need to make.
            </p>
            <p>
              However, here is a basic structure that you can use as a starting point:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
              <div className="p-5 rounded-2xl bg-[#091122] border border-cyan-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-cyan-300">
                  A. Introduction
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Introduce the problem and explain what the article will help the reader understand. Get straight to the point.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#091122] border border-indigo-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-indigo-300">
                  B. Main problem or pain point
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Explain the actual problem the user is facing. Use examples and relevant context to make the problem easy to understand.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#091122] border border-purple-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-purple-300">
                  C. Possible solutions
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Break the problem into relevant scenarios and explain how each situation can be addressed.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#091122] border border-amber-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-amber-300">
                  D. Comparisons and insights
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Help the reader evaluate different approaches using comparisons, practical insights and supporting evidence.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#091122] border border-emerald-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-emerald-300">
                  E. Product relevance
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Where appropriate, explain how your product or service solves a similar problem. Include relevant customer examples, case studies or reviews.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#091122] border border-cyan-500/25 space-y-2">
                <h3 className="font-heading font-bold text-base sm:text-lg text-cyan-300">
                  F. Conclusion
                </h3>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Summarise the important considerations and help the reader understand what decision to make next.
                </p>
              </div>
            </div>

            <p>
              Remember, this is only a starting structure. Some articles may need a step-by-step guide, others may need a comparison table, and some may need a detailed explanation of a particular problem.
            </p>
            <p>
              Don&apos;t force every article into the same template.
            </p>
            <p className="font-semibold text-white">
              A flexible structure that addresses the user&apos;s intent is more important than following a fixed SEO article format.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Principle 10 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              10. Include Expert Insights and a Credible Author Profile
            </h2>
            <p>
              Content should reflect real knowledge and experience.
            </p>
            <p>
              Where the topic requires additional expertise, include insights from people who understand the subject.
            </p>
            <p>For example:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>An HR article can include insights from an experienced HR professional.</li>
              <li>A financial article can include an expert&apos;s explanation of relevant financial considerations.</li>
              <li>A technical article can include insights from someone who has implemented the technology.</li>
            </ul>
            <p>
              You can include expert contributions as short quotes, practical observations or detailed explanations within the article.
            </p>
            <p>Also, maintain a proper author profile.</p>
            <p>An author bio should explain:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/85">
              <li>Who wrote the article.</li>
              <li>Their professional experience.</li>
              <li>Their relevant expertise.</li>
              <li>Why they are qualified to discuss the topic.</li>
            </ul>
            <p>
              Don&apos;t add expert names or credentials simply for appearance. The contribution should be genuine, and the author profile should accurately represent the author&apos;s experience.
            </p>
            <p>
              Real expertise and relevant author information help readers evaluate the credibility of your content and provide useful context about the people behind it.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Final Thoughts */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              Final Thoughts: Write Content That Deserves to Be Referenced
            </h2>
            <p>
              Writing content for better SEO rankings and AI visibility doesn&apos;t require complicated writing techniques.
            </p>
            <p>
              It requires a better understanding of your audience and the problems they are trying to solve.
            </p>
            <p>
              SEO, AEO and GEO may have different optimisation considerations, but the foundation of good content remains the same: relevance, usefulness, clarity, original insights and supporting evidence.
            </p>
            <p className="font-semibold text-white">
              Before starting your next article, ask yourself these questions:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-cyan-200 font-medium">
              <li>Is this a topic my target audience actually wants to know about?</li>
              <li>What decision is the reader trying to make?</li>
              <li>Am I answering the main question directly?</li>
              <li>Have I included useful insights that go beyond existing information?</li>
              <li>Have I covered the possible scenarios and follow-up questions?</li>
              <li>Have I supported my statements with reliable evidence?</li>
              <li>Does my content help the reader evaluate possible solutions?</li>
              <li>Is my product included naturally where it is relevant?</li>
            </ul>
            <p>
              If you can answer these questions confidently, you are moving in the right direction.
            </p>
            <p>
              Don&apos;t write content just to fill your website with articles. Write content that deserves to be read, helps people make decisions and provides information worth referencing.
            </p>
            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-base sm:text-lg shadow-xl my-6 not-italic">
              That is the foundation of creating content that can perform across traditional search and improve your brand&apos;s visibility across AI platforms.
            </blockquote>
          </section>
        </article>

        {/* Interactive Pre-Article Self-Check Widget */}
        <section className="my-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#0c1428] via-[#090e1c] to-[#070b16] border border-cyan-500/30 shadow-2xl relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-cyan-400" />
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                  Interactive Pre-Publishing Checklist
                </h3>
                <p className="text-xs text-white/60 mt-0.5">
                  Test your next article draft against these 8 core questions before hitting publish:
                </p>
              </div>
            </div>
            <span className="text-xs font-mono text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-3 py-1.5 rounded-full w-fit shrink-0">
              {completedCount} of {selfCheckQuestions.length} verified
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {selfCheckQuestions.map((item) => {
              const isChecked = !!checkedQuestions[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => toggleQuestion(item.id)}
                  className={`text-left p-3.5 rounded-xl border transition-all flex items-start gap-3 cursor-pointer ${
                    isChecked
                      ? "bg-cyan-950/40 border-cyan-500/50 text-white shadow-lg shadow-cyan-950/20"
                      : "bg-[#090f1d] border-white/10 hover:border-white/20 text-white/80"
                  }`}
                >
                  <span className="mt-0.5 shrink-0 text-cyan-400">
                    {isChecked ? <CheckSquare className="w-4 h-4 text-emerald-400" /> : <Square className="w-4 h-4 text-white/40" />}
                  </span>
                  <div>
                    <h4 className="text-xs font-bold font-mono text-white mb-0.5">{item.question}</h4>
                    <p className="text-[11px] text-white/60 leading-snug">{item.hint}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs sm:text-sm text-white/75">
              Want a hands-on diagnostic of your existing pages, customer fan-out coverage, and AI citation gaps?
            </p>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
            >
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span>Book Your AI Visibility Audit</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Related Deep Dives & Playbooks */}
        <section className="my-12 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span>Related AI Search &amp; SEO Playbooks</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link
              to="/blog/how-to-do-keyword-research-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">
                  Customer-Intent Framework
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-cyan-400 transition-colors leading-snug">
                  How to Do Keyword Research for AI Visibility
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Learn an 11-step customer-intent-first approach using GSC, Reddit, reviews, and Question Memory to find topics worth writing about.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>Read Playbook</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-to-audit-a-website-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 uppercase">
                  10-Point Technical Checklist
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-indigo-400 transition-colors leading-snug">
                  How Do I Audit My Website for Better AI Visibility?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Audit your website yourself across crawler access, JavaScript rendering parity, fan-out questions, and citations.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-indigo-400 font-semibold">
                <span>Read Audit Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-emerald-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 uppercase">
                  AEO / GEO Strategy
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-emerald-400 transition-colors leading-snug">
                  Why Do I Rank on Google but Don&apos;t Appear in ChatGPT?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Discover why competitors appear in ChatGPT while you don&apos;t, how fan-out queries work, and how to fix crawler blocks.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>Read Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        {/* Author Bio Card */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="p-6 sm:p-7 rounded-3xl bg-[#0a0f1d] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left shadow-2xl">
            <img
              src="/narendra-workstation.jpg"
              alt="Kanna Narendra Kumar"
              className="w-24 h-24 rounded-2xl object-cover object-[center_20%] border border-cyan-500/40 shrink-0 shadow-xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/narendra-avatar.jpg";
              }}
            />
            <div className="space-y-2.5 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-lg text-white">
                    Written by Kanna Narendra Kumar
                  </h4>
                  <p className="text-xs font-mono text-cyan-400">
                    Senior SEO Specialist &amp; AI Search Strategist
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs text-white/80 font-mono transition-colors self-center sm:self-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>LinkedIn Profile</span>
                </a>
              </div>
              <p className="text-xs sm:text-sm text-white/75 leading-relaxed font-sans">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. Pioneer of the Hybrid Engine Optimization (HEO) framework unifying traditional search with generative AI platforms. Available globally for strategic advisory and high-impact enterprise execution.
              </p>
            </div>
          </div>
        </footer>

        {/* Collaboration CTA Footer */}
        <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Zap className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Want Content That Ranks &amp; Gets Cited by AI?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you want to audit your website&apos;s AI search readiness, build a decision-driven content strategy, or scale B2B SaaS inbound leads, let&apos;s connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

